import React, { createContext, useContext, useEffect, useState } from 'react';
import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';
import CartService, { CART_EVENTS } from './cart-service';

const Context = createContext();

export const useCartContext = () => useContext(Context);

const CartContext = ({ children, data }) => {
  const [cartState, setCartState] = useState(CartService.cart);
  const [productsData, setProductsData] = useState({});
  const [loading, setLoading] = useState(CartService.loading);
  const [loadingCartProducts, setLoadingCartProducts] = useState(true);
  const storefront = Storefront.getInstance();
  const decodeId = (id, type) => parseInt(decodeURIComponent(id).split(`gid://shopify/${type}/`)[1], 10);

  useEffect(() => {
    const listener = (event) => {
      setLoading(CartService.loading);
      setCartState(CartService.cart);
      if (event === CART_EVENTS.UPDATE) {
        document.dispatchEvent(new CustomEvent('cart:open'));
        updateCartCount();
      }
    };
    setCartState(CartService.cart);
    CartService.addListener(listener);
    return () => {
      CartService.removeListener(listener);
    };
  }, []);

  const updateCartCount = () => {
    const cartCountEl = document.querySelector('[data-cart-count]');
    const cartQuantity = CartService.cart.item_count || [];
    cartCountEl.setAttribute('data-cart-count', cartQuantity);
    cartCountEl.textContent = cartQuantity;
  };

  useEffect(() => {
    if (cartState.item_count > 0) {
      const getProducts = async (ids) => {
        const response = await storefront.request({
          query: queries.getProductsByIds,
          variables: {
            ids,
          },
        });
        if (!response.data) {
          // eslint-disable-next-line no-console
          console.error('cart/cart-context.js: Storefront API request failed', response);
          return [];
        }
        return Object.fromEntries(response.data.nodes.map((item) => [decodeId(item.id, 'Product'), item]));
      };

      getProducts(cartState.items.map((item) => `gid://shopify/Product/${item.product_id}`)).then((items) => {
        setProductsData(items);
        setLoadingCartProducts(false);
      });
    } else if (cartState.item_count === 0) {
      setProductsData({});
      setLoadingCartProducts(false);
    }
  }, [cartState]);

  /**
   * Wrapper for cart API to make interactions more simple
   */
  const cart = {
    add: (...params) => CartService.add(...params),
    remove: (...params) => CartService.remove(...params),
    update: (...params) => CartService.update(...params),
    state: cartState,
    productsData,
  };

  return (
    <Context.Provider value={{ cart, cartLoading: loading, data, loadingCartProducts }}>{children}</Context.Provider>
  );
};

export const withCartContext = (Rendered) => (props) =>
  (
    <Context.Consumer>
      {(value) => (
        <Rendered
          {...props}
          {...value}
        />
      )}
    </Context.Consumer>
  );

export default CartContext;
